import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import API_BASE_URL from '../config/apiConfig';
import { useAuth } from './AuthContext';
import { resolveProductImageUrl } from '../utils/productImage';

const STORAGE_KEY = 'sk_cart_items';
const AUTH_TOKEN_KEY = 'sk_auth_token';

const readStorage = () => {
  if (typeof window === 'undefined') {
    return [];
  }

  try {
    const authToken = window.localStorage.getItem(AUTH_TOKEN_KEY) || '';
    if (!authToken) {
      return [];
    }

    const rawValue = window.localStorage.getItem(STORAGE_KEY);
    if (!rawValue) {
      return [];
    }

    const parsedValue = JSON.parse(rawValue);
    return Array.isArray(parsedValue) ? parsedValue : [];
  } catch (_) {
    return [];
  }
};

const readAnonymousStorage = () => {
  if (typeof window === 'undefined') {
    return [];
  }

  try {
    const rawValue = window.localStorage.getItem(STORAGE_KEY);
    if (!rawValue) {
      return [];
    }

    const parsedValue = JSON.parse(rawValue);
    return Array.isArray(parsedValue) ? parsedValue : [];
  } catch (_) {
    return [];
  }
};

const writeStorage = (items) => {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  } catch (_) {
    // ignore storage write errors
  }
};

const readInitialCart = () => {
  if (typeof window === 'undefined') {
    return [];
  }

  const authToken = window.localStorage.getItem(AUTH_TOKEN_KEY) || '';
  return authToken ? readStorage() : readAnonymousStorage();
};

const CartContext = createContext(undefined);

const toCartItem = (item) => ({
  ProductId: Number(item.ProductId),
  ProductName: item.ProductName || 'Product',
  Price: Number(item.Price || item.UnitPrice) || 0,
  OldPrice: Number(item.OldPrice) || 0,
  DefaultImage: resolveProductImageUrl(
    item.DefaultImage ||
    item.ProductImage ||
    (Array.isArray(item.Images) && item.Images[0] && item.Images[0].ImageUrl)
  ),
  SubCategoryName: item.SubCategoryName || '',
  quantity: Math.max(1, Number(item.quantity || item.Quantity || 1)),
});

const mapServerItemToCartItem = (item) => ({
  ProductId: Number(item.ProductId),
  ProductName: item.ProductName || 'Product',
  Price: Number(item.Price) || 0,
  OldPrice: 0,
  DefaultImage: resolveProductImageUrl(item.DefaultImage),
  SubCategoryName: '',
  quantity: Math.max(1, Number(item.quantity || 1)),
});

export const CartProvider = ({ children }) => {
  const { isAuthenticated, token, user } = useAuth();
  const [cartItems, setCartItems] = useState(() => readInitialCart());
  const serverCartReadyRef = useRef(false);
  const syncedUserIdRef = useRef(0);
  const syncInProgressRef = useRef(false);

  const ecRequest = useCallback(
    async (path, options = {}) => {
      const headers = {
        'Content-Type': 'application/json',
        ...(options.headers || {}),
      };

      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }

      const response = await fetch(`${API_BASE_URL}${path}`, {
        ...options,
        headers,
      });

      const result = await response.json();

      if (!response.ok || !result?.success) {
        throw new Error(result?.message || 'Request failed');
      }

      return result;
    },
    [token]
  );

  useEffect(() => {
    writeStorage(cartItems);
  }, [cartItems]);

  useEffect(() => {
    const userId = Number(user?.UserId || 0);

    if (!isAuthenticated || !token || userId <= 0) {
      serverCartReadyRef.current = false;
      syncedUserIdRef.current = 0;
      setCartItems(readAnonymousStorage());
      return;
    }

    let isCancelled = false;

    const hydrateServerCart = async () => {
      try {
        serverCartReadyRef.current = false;

        const currentAnonymousItems = readAnonymousStorage();
        if (syncedUserIdRef.current !== userId && currentAnonymousItems.length) {
          for (const item of currentAnonymousItems) {
            await ecRequest('/ec/cart/save', {
              method: 'POST',
              body: JSON.stringify({
                ProductId: item.ProductId,
                ProductName: item.ProductName,
                ProductImage: item.DefaultImage,
                Price: Number(item.Price || 0),
                quantity: Number(item.quantity || 1),
              }),
            });
          }
        }

        const response = await ecRequest('/ec/cart/getByUser', { method: 'GET' });
        const serverItems = Array.isArray(response.data) ? response.data.map(mapServerItemToCartItem) : [];

        if (!isCancelled) {
          syncedUserIdRef.current = userId;
          setCartItems(serverItems);
          serverCartReadyRef.current = true;
        }
      } catch (error) {
        if (!isCancelled) {
          console.error('Failed to load server cart:', error);
          serverCartReadyRef.current = true;
        }
      }
    };

    hydrateServerCart();

    return () => {
      isCancelled = true;
    };
  }, [isAuthenticated, token, user?.UserId, ecRequest]);

  useEffect(() => {
    const userId = Number(user?.UserId || 0);

    if (!isAuthenticated || !token || userId <= 0 || !serverCartReadyRef.current) {
      return;
    }

    if (syncInProgressRef.current) {
      return;
    }

    let isCancelled = false;

    const syncServerCart = async () => {
      try {
        syncInProgressRef.current = true;

        await ecRequest('/ec/cart/delete', {
          method: 'POST',
          body: JSON.stringify({}),
        });

        for (const item of cartItems) {
          await ecRequest('/ec/cart/save', {
            method: 'POST',
            body: JSON.stringify({
              ProductId: item.ProductId,
              ProductName: item.ProductName,
              ProductImage: item.DefaultImage,
              Price: Number(item.Price || 0),
              quantity: Number(item.quantity || 1),
            }),
          });
        }
      } catch (error) {
        if (!isCancelled) {
          console.error('Failed to sync server cart:', error);
        }
      } finally {
        syncInProgressRef.current = false;
      }
    };

    syncServerCart();

    return () => {
      isCancelled = true;
    };
  }, [cartItems, isAuthenticated, token, user?.UserId, ecRequest]);

  const addToCart = useCallback((product, quantity = 1) => {
    if (!product || !product.ProductId || quantity < 1) {
      return;
    }

    setCartItems((previousItems) => {
      const normalizedProduct = toCartItem({
        ...product,
        quantity,
      });
      const existingIndex = previousItems.findIndex((item) => item.ProductId === normalizedProduct.ProductId);

      if (existingIndex >= 0) {
        const updatedItems = [...previousItems];
        updatedItems[existingIndex] = {
          ...updatedItems[existingIndex],
          quantity: updatedItems[existingIndex].quantity + quantity,
        };
        return updatedItems;
      }

      return [...previousItems, normalizedProduct];
    });
  }, []);

  const removeFromCart = useCallback((productId) => {
    setCartItems((previousItems) => previousItems.filter((item) => item.ProductId !== productId));
  }, []);

  const updateQuantity = useCallback((productId, quantity) => {
    if (quantity < 1) {
      return;
    }

    setCartItems((previousItems) =>
      previousItems.map((item) =>
        item.ProductId === productId
          ? {
              ...item,
              quantity,
            }
          : item
      )
    );
  }, []);

  const clearCart = useCallback(() => {
    setCartItems([]);
  }, []);

  const cartCount = useMemo(
    () => cartItems.length,
    [cartItems]
  );

  const cartTotal = useMemo(
    () => cartItems.reduce((accumulator, item) => accumulator + Number(item.Price || 0) * Number(item.quantity || 0), 0),
    [cartItems]
  );

  const value = useMemo(
    () => ({
      cartItems,
      cartCount,
      cartTotal,
      addToCart,
      removeFromCart,
      updateQuantity,
      clearCart,
    }),
    [cartItems, cartCount, cartTotal, addToCart, removeFromCart, updateQuantity, clearCart]
  );

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within CartProvider');
  }
  return context;
};

export default CartContext;
